import type { Control } from 'react-hook-form'
import { z } from 'zod'
import { optionalText, regexField, requiredText } from '../../../lib/forms/rules'
import { COLUMN_BYTES } from '../../../lib/columnBytes'

/** 24 saat biçimi SS:DD (00:00–23:59) — `FormTimeSelectField`'in yazdığı biçim. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/** Ürün sınırları (karakter) — sözleşme: başlık 200, detay 500. */
export const AGENDA_TITLE_MAX = 200
export const AGENDA_DESCRIPTION_MAX = 500

/** Yeni davetin varsayılan saatleri (30 dakika). */
export const DEFAULT_START_TIME = '08:00'
export const DEFAULT_END_TIME = '08:30'

/** `AgendaParticipant` ile aynı biçim. */
const participantSchema = z.object({
  employeeId: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  businessMail: z.string().nullish(),
  jobTitleTr: z.string().nullish(),
  jobTitleEn: z.string().nullish(),
  managerListId: z.number().nullish(),
})

/**
 * Tarih + saat → duvar saati ms (UTC alanlarıyla; yerel `Date` aritmetiği yaz saati geçişinde bir
 * saat kaydırırdı). Geçersiz parçada `null`.
 */
export function wallClockMs(date: string, time: string): number | null {
  const d = DATE_RE.exec(date)
  if (!d || !TIME_RE.test(time)) return null
  const [h, m] = time.split(':').map(Number)
  return Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), h, m)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** {@link wallClockMs}'in tersi → form parçaları. */
function fromWallClockMs(ms: number): { date: string; time: string } {
  const d = new Date(ms)
  return {
    date: `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
    time: `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`,
  }
}

/**
 * Başlangıç `prev` → `next` kayınca bitişin yeni değeri (süre korunur). Önceki başlangıç ya da bitiş
 * bozuksa veya başlangıç değişmediyse `null` — bitişe dokunulmaz.
 */
export function shiftedEnd(
  prev: number | null,
  next: number,
  v: { endDate: string; endTime: string },
): { date: string; time: string } | null {
  const end = wallClockMs(v.endDate, v.endTime)
  if (prev === null || end === null || prev === next) return null
  return fromWallClockMs(end + (next - prev))
}

/**
 * Toplantı formu. Bitiş başlangıçtan sonra olmalı (tarih+saat birlikte; parça bozuksa denetlenmez).
 * "Başlangıç şu andan sonra" kuralı istemcide denetlenmez — "şimdi" sunucunun saatidir.
 */
export const agendaFormSchema = z
  .object({
    title: requiredText(AGENDA_TITLE_MAX, COLUMN_BYTES.agendaTitle),
    description: optionalText(AGENDA_DESCRIPTION_MAX, COLUMN_BYTES.agendaDescription),
    startDate: requiredText(),
    startTime: regexField(TIME_RE, 'validation.time'),
    endDate: requiredText(),
    endTime: regexField(TIME_RE, 'validation.time'),
    participants: z.array(participantSchema).min(1, 'validation.required'),
    /** "Tekrarlı Toplantı" (126) — oluşturmada onay kutusu; "Serinin tamamı"nda hep `true`. */
    recurring: z.boolean(),
    recurrenceType: z.enum(['DAILY', 'WEEKLY', 'MONTHLY']).nullable(),
    /** `YYYY-MM-DD`; boşsa `''`. */
    recurrenceEndDate: z.string(),
    /** "Serinin tamamı" düzenlemesi: bitiş tarihi düzenlenen toplantının gününe eşit olabilir. */
    seriesEdit: z.boolean(),
    /** Açılıştaki seri bitişi — değişmediyse yeniden doğrulanmaz (FR-003 yalnız değişikliğe uygulanır). */
    originalRecurrenceEndDate: z.string(),
    /** Seriye bağlı toplantı ("Yalnız bu" ya da "Serinin tamamı"): aynı gün kuralı (FR-004) geçerli. */
    seriesMember: z.boolean(),
  })
  .refine(
    (v) => {
      const start = wallClockMs(v.startDate, v.startTime)
      const end = wallClockMs(v.endDate, v.endTime)
      return start === null || end === null || end > start
    },
    { path: ['endTime'], message: 'validation.timeRange' },
  )
  .superRefine((v, ctx) => {
    // "Yalnız bu toplantı" da tekrarlıdır: iki güne yayılırsa sonraki "Serinin tamamı" kilitli gün
    // alanlarıyla kaydedilemezdi (push kapısı review'ü).
    const sameDayRule = v.recurring || v.seriesMember
    if (sameDayRule && DATE_RE.test(v.startDate) && DATE_RE.test(v.endDate) && v.startDate !== v.endDate) {
      ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'agenda.validation.recurrenceSameDay' })
    }
    if (!v.recurring) return
    if (!v.recurrenceType) {
      ctx.addIssue({ code: 'custom', path: ['recurrenceType'], message: 'validation.required' })
    }
    const endIssue = recurrenceEndIssue(v)
    if (endIssue) ctx.addIssue({ code: 'custom', path: ['recurrenceEndDate'], message: endIssue })
  })

/**
 * Tekrar bitiş tarihinin kuralı (FR-003): oluşturmada ilk günden **sonra**, seri düzenlemesinde düzenlenen
 * günden önce değil; ikisinde de en çok 1 yıl. `YYYY-MM-DD` dizgeleri sözlük sırasıyla karşılaştırılır.
 * Sunucu aynı kuralı yeniden doğrular; buradaki yalnız erken geri bildirimdir.
 */
function recurrenceEndIssue(v: {
  startDate: string
  recurrenceEndDate: string
  seriesEdit: boolean
  originalRecurrenceEndDate: string
}): string | null {
  const end = v.recurrenceEndDate
  if (!DATE_RE.test(end)) return 'validation.required'
  // Değişmeyen bitiş yeniden doğrulanmaz — sunucu da aynı kuralı uygular (`resolveNewEnd`).
  if (v.seriesEdit && end === v.originalRecurrenceEndDate) return null
  if (!DATE_RE.test(v.startDate)) return null
  if (v.seriesEdit && end < v.startDate) return 'agenda.validation.recurrenceEndBeforeOccurrence'
  if (!v.seriesEdit && end <= v.startDate) return 'agenda.validation.recurrenceEndAfterStart'
  return end > plusOneYear(v.startDate) ? 'agenda.validation.recurrenceMaxYear' : null
}

/** `YYYY-MM-DD` + 1 yıl (29 Şubat'ta sunucu 28 Şubat'a sıkıştırır; karşılaştırma sonucu değişmez). */
function plusOneYear(iso: string): string {
  return `${Number(iso.slice(0, 4)) + 1}${iso.slice(4)}`
}

export type AgendaFormValues = z.infer<typeof agendaFormSchema>

/** Formun RHF denetimi — form bileşenleri arasında paylaşılır. */
export type AgendaControl = Control<AgendaFormValues, unknown, AgendaFormValues>

/** Tarih (`YYYY-MM-DD`) + saat (`SS:DD`) → bölgesiz duvar saati `yyyy-MM-ddTHH:mm:00`. */
export function toLocalDateTime(date: string, time: string): string {
  return `${date}T${time}:00`
}

/** `yyyy-MM-ddTHH:mm[:ss]` → form parçaları; {@link toLocalDateTime}'ın tersi. */
export function splitLocalDateTime(value: string): { date: string; time: string } {
  return { date: value.slice(0, 10), time: value.slice(11, 16) }
}
